import axios from 'axios';
import {
  CheckCircle,
  Download,
  FileText,
  Folder,
  FolderOpen,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type {
  ApproveRequest,
  BulkApproveRequest,
  BulkRejectRequest,
  PaginatedResponse,
  PaginationParams,
  PendingDocument,
  PendingDocumentStats,
  RejectRequest,
} from '@/types/external-document.types';

// ─────────────────────────────────────────────────────────────────────────────────
// Service functions (inlined from external-document.service.ts)
// ─────────────────────────────────────────────────────────────────────────────────

const API_ENDPOINT_BASE = '/api/external-documents';

async function getPendingDocuments(
  apiBaseUrl: string,
  params: PaginationParams,
): Promise<PaginatedResponse<PendingDocument>> {
  const response = await axios.get(`${apiBaseUrl}${API_ENDPOINT_BASE}/pending`, {
    params: {
      page: params.page,
      limit: params.limit,
      keyword: params.keyword,
      status: params.status,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
      folderPath: params.folderPath,
    },
  });
  return response.data;
}

async function approveDocument(
  apiBaseUrl: string,
  request: ApproveRequest,
): Promise<{ success: boolean; message: string }> {
  const response = await axios.post(`${apiBaseUrl}${API_ENDPOINT_BASE}/approve`, request);
  return response.data;
}

async function rejectDocument(
  apiBaseUrl: string,
  request: RejectRequest,
): Promise<{ success: boolean; message: string }> {
  const response = await axios.post(`${apiBaseUrl}${API_ENDPOINT_BASE}/reject`, request);
  return response.data;
}

async function bulkApproveDocuments(
  apiBaseUrl: string,
  request: BulkApproveRequest,
): Promise<{ success: boolean; message: string }> {
  const response = await axios.post(`${apiBaseUrl}${API_ENDPOINT_BASE}/bulk-approve`, request);
  return response.data;
}

async function bulkRejectDocuments(
  apiBaseUrl: string,
  request: BulkRejectRequest,
): Promise<{ success: boolean; message: string }> {
  const response = await axios.post(`${apiBaseUrl}${API_ENDPOINT_BASE}/bulk-reject`, request);
  return response.data;
}

async function getPendingDocumentStats(apiBaseUrl: string): Promise<PendingDocumentStats> {
  const response = await axios.get(`${apiBaseUrl}${API_ENDPOINT_BASE}/stats`);
  return response.data;
}

// ─────────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('vi-VN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Extract folder path segments for breadcrumb
function getBreadcrumbPaths(folderPath: string): Array<{ label: string; path: string }> {
  if (!folderPath) return [];
  const segments = folderPath.split('/').filter(Boolean) as string[];
  const breadcrumbs: Array<{ label: string; path: string }> = [];
  let currentPath = '';

  for (const seg of segments) {
    currentPath = currentPath ? `${currentPath}/${seg}` : seg;
    breadcrumbs.push({ label: seg, path: currentPath });
  }

  return breadcrumbs;
}

// ─────────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────────

function ApproveConfirmDialog({
  open,
  onClose,
  onConfirm,
  document,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  document: PendingDocument | null;
  loading: boolean;
}) {
  if (!document) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Xác nhận duyệt tài liệu</DialogTitle>
          <DialogDescription>
            Bạn có chắc chắn muốn duyệt tài liệu "<strong>{document.fileName}</strong>"?
            <div className="mt-2 text-xs text-gray-500">
              Đường dẫn đích:{' '}
              <code className="bg-gray-100 px-1 rounded">
                {document.targetPath.replace('pending_approval_external/', 'kho_doi_ngoai/')}
              </code>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Hủy
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Xác nhận'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectConfirmDialog({
  open,
  onClose,
  onConfirm,
  document,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  document: PendingDocument | null;
  loading: boolean;
  rejectionReason: string;
  setRejectionReason: (v: string) => void;
}) {
  if (!document) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-red-600">Xác nhận từ chối tài liệu</DialogTitle>
          <DialogDescription>
            Tài liệu "<strong>{document.fileName}</strong>" sẽ bị xóa khỏi vùng chờ duyệt.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Hủy
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Xác nhận từ chối'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyPendingState({ isFetching }: { isFetching: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <CheckCircle className="h-12 w-12 text-green-400" />
      <h3 className="mt-4 text-lg font-medium">Không có tài liệu nào cần duyệt</h3>
      <p className="mt-1 text-sm text-gray-500">
        {isFetching ? 'Đang tải...' : 'Tất cả tài liệu đã được duyệt hết.'}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────────

export function PendingApprovalPage({
  currentPrefix,
  apiBaseUrl,
  isUploading,
}: {
  currentPrefix: string;
  apiBaseUrl: string;
  isUploading: boolean;
}) {
  // Check if user has admin role (Master_admin)
  const hasAdminRole = useMemo(() => {
    try {
      const dataLogin = localStorage.getItem('dataLogin');
      if (!dataLogin) return false;
      const parsed: any = JSON.parse(dataLogin);
      return parsed?.user?.groupname === 'Master_admin';
    } catch {
      return false;
    }
  }, []);

  const isExternalPath =
    currentPrefix.startsWith('kho_doi_ngoai/') || currentPrefix === 'kho_doi_ngoai/';

  // Return null if not external path or user doesn't have admin role
  if (!isExternalPath || !hasAdminRole) {
    return null;
  }

  // State
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [documents, setDocuments] = useState<PendingDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [totalAllItems, setTotalAllItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isBulkRejecting, setIsBulkRejecting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [activeDocument, setActiveDocument] = useState<PendingDocument | null>(null);

  // Folder navigation state
  const [currentFolderPath, setCurrentFolderPath] = useState<string>(''); // Empty = root level

  // Reset page and selection when folder path changes
  useEffect(() => {
    setPage(1);
    setSelectedKeys(new Set());
  }, [currentFolderPath]);

  // Fetch pending documents from API
  const fetchPendingDocuments = async () => {
    setIsLoading(true);
    try {
      const response: PaginatedResponse<PendingDocument> = await getPendingDocuments(apiBaseUrl, {
        page,
        limit: 20,
        keyword: keyword || undefined,
        folderPath: currentFolderPath || undefined,
      });
      setDocuments(response.data);
      setTotal(response.pagination.total);
      setTotalAllItems(response.pagination.totalAllItems);
    } catch (error) {
      console.error('Failed to fetch pending documents:', error);
      toast.error('Không thể tải danh sách tài liệu');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      await getPendingDocumentStats(apiBaseUrl);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    if (!isUploading) {
      fetchPendingDocuments();
      fetchStats();
    }
  }, [page, keyword, currentFolderPath, isUploading]);

  const totalPages = Math.max(1, Math.ceil(total / 20));

  // Filter by keyword - match backend fields
  const filteredDocuments = useMemo(() => {
    if (!keyword) return documents;
    return documents.filter(
      (doc) =>
        doc.fileName.toLowerCase().includes(keyword.toLowerCase()) ||
        doc.tempPath.toLowerCase().includes(keyword.toLowerCase()) ||
        doc.targetPath.toLowerCase().includes(keyword.toLowerCase()) ||
        doc.modifiedBy?.toLowerCase().includes(keyword.toLowerCase()) ||
        doc.creator?.toLowerCase().includes(keyword.toLowerCase()),
    );
  }, [documents, keyword]);

  // Get only selectable documents (exclude folders)
  const selectableDocuments = useMemo(
    () => filteredDocuments.filter((doc) => !doc.isFolder),
    [filteredDocuments],
  );

  // ── Folder Navigation Actions ──

  const handleFolderClick = (doc: PendingDocument) => {
    const cleanPath = doc.tempPath.endsWith('/') ? doc.tempPath.slice(0, -1) : doc.tempPath;
    setCurrentFolderPath(cleanPath);
    setKeyword('');
  };

  // const handleGoBack = () => {
  //   if (!currentFolderPath) return;

  //   const cleanPath = currentFolderPath.endsWith('/')
  //     ? currentFolderPath.slice(0, -1)
  //     : currentFolderPath;

  //   const paths = cleanPath.split('/').filter(Boolean);
  //   if (paths.length <= 1) {
  //     setCurrentFolderPath('');
  //   } else {
  //     paths.pop();
  //     setCurrentFolderPath(paths.join('/'));
  //   }
  //   setKeyword('');
  // };

  // ── Document Actions ──

  const handleApprove = (doc: PendingDocument) => {
    setActiveDocument(doc);
    setApproveDialogOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!activeDocument) return;
    setIsApproving(true);
    try {
      const metadataPath = activeDocument.tempPath + '.metadata.json';
      const targetPath = activeDocument.targetPath || '';
      const result = await approveDocument(apiBaseUrl, {
        metadataPath,
        targetPath,
      });
      toast.success(result.message || 'Duyệt tài liệu thành công');
      setDocuments((prev) => prev.filter((d) => d.tempPath !== activeDocument.tempPath));
      setTotal((prev) => prev - 1);
      fetchStats();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Không thể duyệt tài liệu');
    } finally {
      setIsApproving(false);
      setApproveDialogOpen(false);
      setActiveDocument(null);
    }
  };

  const handleReject = (doc: PendingDocument) => {
    setActiveDocument(doc);
    setRejectionReason('');
    setRejectDialogOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!activeDocument) return;
    setIsRejecting(true);
    try {
      const metadataPath = activeDocument.tempPath + '.metadata.json';
      const result = await rejectDocument(apiBaseUrl, {
        metadataPath,
        reason: rejectionReason,
      } as RejectRequest);
      toast.success(result.message || 'Từ chối tài liệu thành công');
      setDocuments((prev) => prev.filter((d) => d.tempPath !== activeDocument.tempPath));
      setTotal((prev) => prev - 1);
      fetchStats();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Không thể từ chối tài liệu');
    } finally {
      setIsRejecting(false);
      setRejectDialogOpen(false);
      setActiveDocument(null);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedKeys.size === 0) return;
    setIsBulkApproving(true);
    try {
      const metadataPaths = Array.from(selectedKeys).map((key) => key + '.metadata.json');
      const result = await bulkApproveDocuments(apiBaseUrl, {
        metadataPaths,
      } as BulkApproveRequest);
      toast.success(result.message || `Đã duyệt ${selectedKeys.size} tài liệu`);
      setSelectedKeys(new Set());
      await fetchPendingDocuments();
      await fetchStats();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Không thể duyệt hàng loạt');
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedKeys.size === 0) return;
    setIsBulkRejecting(true);
    try {
      const metadataPaths = Array.from(selectedKeys).map((key) => key + '.metadata.json');
      const result = await bulkRejectDocuments(apiBaseUrl, {
        metadataPaths,
        reason: rejectionReason,
      } as BulkRejectRequest);
      toast.success(result.message || `Đã từ chối ${selectedKeys.size} tài liệu`);
      setSelectedKeys(new Set());
      await fetchPendingDocuments();
      await fetchStats();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Không thể từ chối hàng loạt');
    } finally {
      setIsBulkRejecting(false);
    }
  };

  // const handleExpandTable = () => {
  //   setShowTableDialog(true);
  // };

  const handleRefresh = async () => {
    setIsFetching(true);
    try {
      await Promise.all([fetchPendingDocuments(), fetchStats()]);
      toast.success('Đã tải lại dữ liệu');
    } catch {
      toast.error('Không thể tải lại dữ liệu');
    } finally {
      setIsFetching(false);
    }
  };

  // Build breadcrumb path segments
  const breadcrumbPaths = useMemo(() => {
    return getBreadcrumbPaths(currentFolderPath);
  }, [currentFolderPath]);

  // ── Download Handler ──
  const handleDownload = async (paths: string[]) => {
    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/download`,
        { paths },
        { responseType: 'blob' },
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition =
        response.headers['Content-Disposition'] || response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : 'download.zip';

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`Đã bắt đầu tải xuống ${paths.length} mục`);
    } catch (error) {
      console.error('Error downloading files:', error);
      toast.error('Không thể tải xuống tập tin');
    }
  };

  const isInFolder = currentFolderPath.length > 0;

  // ── Render ──
  return (
    <>
      <div className="border rounded-lg bg-blue-50/50 overflow-hidden">
        <div className="h-[32px] px-3">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-2 mr-2 flex-1">
              {/* Folder Navigation Breadcrumb */}
              <div className="flex items-center gap-1">
                <FileText className="h-4 w-4 text-blue-600" />
              </div>

              <Badge variant="secondary">Tài liệu chờ duyệt ({totalAllItems})</Badge>
            </div>

            <div className="flex gap-2">
              <Dialog open={showTableDialog} onOpenChange={setShowTableDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isFetching || isLoading}
                    className="h-7 px-2 text-xs"
                  >
                    <Folder className="h-3.5 w-3.5" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Danh sách tài liệu chờ duyệt</DialogTitle>
                    <DialogDescription>
                      {total} tài liệu cần duyệt
                      {isInFolder && (
                        <span className="block mt-1 text-blue-600 font-medium">
                          <>
                            <div className="flex items-center gap-1">
                              Thư mục:{' '}
                              {breadcrumbPaths.map((bp, idx) => (
                                <span key={bp.path} className="flex items-center">
                                  <button
                                    onClick={() => setCurrentFolderPath(bp.path)}
                                    className="h-6 px-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded transition-colors"
                                  >
                                    {bp.label === 'pending_approval_external'
                                      ? 'kho_doi_ngoai'
                                      : bp.label}
                                  </button>
                                  {idx < breadcrumbPaths.length - 1 && (
                                    <span className="text-gray-400 mx-1">/</span>
                                  )}
                                </span>
                              ))}
                            </div>
                          </>
                        </span>
                      )}
                    </DialogDescription>
                  </DialogHeader>

                  {/* Search & Bulk Actions Bar */}
                  <div className="p-3 -mx-6 px-6 border-b bg-gray-50 -mt-[1px] flex items-center gap-3">
                    <input
                      type="text"
                      placeholder="Tìm kiếm tên file, đường dẫn..."
                      value={keyword}
                      onChange={(e) => {
                        setKeyword(e.target.value);
                        setPage(1);
                      }}
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />

                    {selectedKeys.size > 0 && (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">Đã chọn: {selectedKeys.size}</Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleBulkApprove}
                          disabled={isBulkApproving}
                          className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                        >
                          Duyệt chọn
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleBulkReject}
                          disabled={isBulkRejecting}
                          className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                        >
                          Từ chối chọn
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Table Content - Scrollable */}
                  <div className="max-h-[60vh] overflow-auto mt-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox
                              checked={
                                selectableDocuments.length > 0 &&
                                selectedKeys.size === selectableDocuments.length &&
                                selectableDocuments.every((doc) => selectedKeys.has(doc.tempPath))
                              }
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedKeys(
                                    new Set(selectableDocuments.map((d) => d.tempPath)),
                                  );
                                } else {
                                  setSelectedKeys(new Set());
                                }
                              }}
                            />
                          </TableHead>
                          <TableHead>Tên file</TableHead>
                          <TableHead>Kích thước</TableHead>
                          <TableHead>Người tạo</TableHead>
                          <TableHead>Ngày sửa</TableHead>
                          <TableHead>Trạng thái</TableHead>
                          <TableHead className="text-right">Thao tác</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isLoading ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8">
                              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                            </TableCell>
                          </TableRow>
                        ) : filteredDocuments.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7}>
                              <EmptyPendingState isFetching={isFetching} />
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredDocuments.map((doc) => (
                            <TableRow
                              key={doc.metadataPath || doc.tempPath || doc.id}
                              className={doc.isFolder ? 'cursor-pointer hover:bg-blue-50' : ''}
                              onClick={() => doc.isFolder && handleFolderClick(doc)}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={doc.isFolder ? false : selectedKeys.has(doc.tempPath)}
                                  onCheckedChange={(checked) => {
                                    if (checked && !doc.isFolder) {
                                      setSelectedKeys((prev) => new Set(prev).add(doc.tempPath));
                                    }
                                  }}
                                  disabled={doc.isFolder}
                                />
                              </TableCell>
                              <TableCell className="max-w-xs">
                                <div className="flex items-center gap-2 truncate">
                                  {doc.isFolder ? (
                                    <Folder className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                  ) : (
                                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                  )}
                                  <span className="font-medium">{doc.fileName}</span>
                                  {doc.isFolder && (
                                    <span className="text-xs text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">
                                      folder
                                    </span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>{doc.isFolder ? '-' : formatFileSize(doc.size)}</TableCell>
                              <TableCell>{doc.modifiedBy || '-'}</TableCell>
                              <TableCell>
                                {doc.modifiedAt ? formatDate(doc.modifiedAt) : '-'}
                              </TableCell>
                              <TableCell>
                                {doc.isFolder ? (
                                  <Badge
                                    variant="outline"
                                    className="bg-blue-50 text-blue-700 border-blue-200"
                                  >
                                    <FolderOpen className="h-3 w-3 mr-1" />
                                    Thư mục
                                  </Badge>
                                ) : (
                                  <Badge
                                    variant="outline"
                                    className="bg-yellow-50 text-yellow-700 border-yellow-200"
                                  >
                                    Chờ duyệt
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {doc.isFolder ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleFolderClick(doc);
                                          }}
                                          className="text-blue-600 hover:bg-blue-50"
                                        >
                                          <FolderOpen className="h-4 w-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Mở thư mục</TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <TooltipProvider>
                                    <div className="flex items-center justify-end gap-1">
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDownload([doc.tempPath]);
                                            }}
                                          >
                                            <Download className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Tải xuống</TooltipContent>
                                      </Tooltip>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleApprove(doc);
                                            }}
                                            className="text-green-600 hover:bg-green-50"
                                          >
                                            <CheckCircle className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Duyệt</TooltipContent>
                                      </Tooltip>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleReject(doc);
                                            }}
                                            className="text-red-600 hover:bg-red-50"
                                          >
                                            <XCircle className="h-4 w-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Từ chối</TooltipContent>
                                      </Tooltip>
                                    </div>
                                  </TooltipProvider>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {!isLoading && total > 20 && (
                    <div className="flex items-center justify-between p-3 border-t -mx-6 px-6 bg-gray-50">
                      <p className="text-sm text-gray-500">
                        Trang {page} / {totalPages}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page <= 1}
                        >
                          Trước
                        </Button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => (
                          <Button
                            key={i + 1}
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(i + 1)}
                            className={
                              page === i + 1 ? 'bg-blue-600 text-white border-blue-600' : ''
                            }
                          >
                            {i + 1}
                          </Button>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages}
                        >
                          Sau
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isFetching || isLoading}
                className="h-7 px-2 text-xs"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <ApproveConfirmDialog
        open={approveDialogOpen}
        onClose={() => {
          setApproveDialogOpen(false);
          setActiveDocument(null);
        }}
        onConfirm={handleConfirmApprove}
        document={activeDocument}
        loading={isApproving}
      />
      <RejectConfirmDialog
        open={rejectDialogOpen}
        onClose={() => {
          setRejectDialogOpen(false);
          setActiveDocument(null);
        }}
        onConfirm={handleConfirmReject}
        document={activeDocument}
        loading={isRejecting}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
      />
    </>
  );
}
